import React from "react";

/**
 * ScorePill — compact 0-100 Moonbucks grade for high-density surfaces
 * (dashboard alert cards, screener rows, admin queue). The FULL
 * `MoonbucksScoreCard` lives on the stock detail page; this is the
 * same language in a one-line format.
 *
 * Accepts either the full grade object (with `score` + `label` + `color`)
 * or degrades to a simple neutral pill when nothing is passed.
 */
export default function ScorePill({ grade, size = "sm" }) {
  if (!grade || typeof grade.score !== "number") return null;
  const style = PILL_STYLES[grade.color] || PILL_STYLES.amber;
  const scoreText = Math.round(grade.score);
  const isXs = size === "xs";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md font-mono-data font-bold ${isXs ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"}`}
      style={{
        color: style.text,
        background: style.bg,
        border: `1px solid ${style.border}`,
        letterSpacing: "0.06em",
      }}
      title={`Moonbucks Score ${scoreText}/100 · ${grade.label}${grade.lightweight ? " (quick read)" : ""}`}
      data-testid={`score-pill-${grade.label}`}
    >
      <span
        className="inline-block rounded-full"
        style={{
          width: 6,
          height: 6,
          background: style.dot,
          boxShadow: `0 0 8px ${style.dot}88`,
        }}
      />
      <span>{scoreText}</span>
      <span className="opacity-70 tracking-wider uppercase">{grade.label}</span>
    </span>
  );
}

const PILL_STYLES = {
  emerald: {
    text: "#B7F7DA",
    bg: "rgba(56, 231, 163, 0.14)",
    border: "rgba(56, 231, 163, 0.45)",
    dot: "#38E7A3",
  },
  green: {
    text: "#C4F4D3",
    bg: "rgba(74, 222, 128, 0.14)",
    border: "rgba(74, 222, 128, 0.4)",
    dot: "#4ADE80",
  },
  amber: {
    text: "#FCE9C3",
    bg: "rgba(245, 166, 35, 0.14)",
    border: "rgba(245, 166, 35, 0.45)",
    dot: "#F5A623",
  },
  orange: {
    text: "#FED4BF",
    bg: "rgba(255, 138, 91, 0.14)",
    border: "rgba(255, 138, 91, 0.45)",
    dot: "#FF8A5B",
  },
  red: {
    text: "#FBC5C5",
    bg: "rgba(255, 92, 92, 0.14)",
    border: "rgba(255, 92, 92, 0.45)",
    dot: "#FF5C5C",
  },
};
